// reportDownload.js
//
// The client half of the three corridor-report endpoints #131 shipped (#143).
//
// CORRIDOR_REPORT.md §7.1 states the constraint this module exists for: an
// `<a href>` cannot fetch these files. A browser attaches no `Authorization`
// header to a navigation and all three paths are in SUPERUSER_GET_PATHS, so the
// only route is apiFetch → blob → object URL → synthetic click.
//
// That makes the filename the client's problem, which it must not be: two
// reports of different periods collide the moment the client invents a name. So
// the name is read back off the `Content-Disposition` the server already set it
// on, and constructed here only as a fallback for the one case where the header
// is unreadable.
//
// Split out of Sidebar.jsx for the reason chartExport.js is split out of
// TowerDriftChart: the two parts worth testing — the name the server chose and
// the period the operator picked — need no DOM.

import { apiFetch } from './apiFetch';
import { downloadBlob } from './chartExport';

const DAY_MS = 86_400_000;

// Mirrors MAX_PERIOD_DAYS in DataIngestionESM/lib/report.mjs, which answers a
// longer period with a 400. Checked here as well so an out-of-range period is
// refused beside the field that set it, rather than after a round trip the
// operator would read as "the report failed".
//
// **Not** the chart rule, and deliberately not replaced by it. #184 removed the
// charts' six-month cap in favour of a 2026-01-01 floor (src/chartRange.js) and
// left this where it is: a chart is a downsampled read whose response size does
// not grow with the period, and a report renders a PDF whose cost does. The two
// were the same number by coincidence. Sharing one would mean widening a chart
// silently widens the report, which is the defect #184 was opened about.
export const MAX_PERIOD_DAYS = 183;

export const REPORT_FORMATS = {
  pdf: { path: 'report.pdf', extension: 'pdf' },
  csv: { path: 'report.csv', extension: 'csv' },
};

/**
 * The translation key for why this period cannot be asked for, or null when it
 * can.
 *
 * A key rather than a sentence, so the caller renders it in the operator's
 * language.
 *
 * The rules are parsePeriod()'s in lib/report.mjs, not TowerDriftChart's. A
 * date-only `end` there is the *whole* UTC day, so `start === end` is a valid
 * one-day period. TowerDriftChart rejects that pair (`days < 1`) — correct for
 * a chart, whose custom range would otherwise cover an instant — but copying
 * its check here would refuse "give me yesterday's report".
 */
export function periodError(from, to) {
  if (!from || !to) return 'enterBothDates';

  const startMs = Date.parse(`${from}T00:00:00.000Z`);
  const endMs = Date.parse(`${to}T00:00:00.000Z`);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return 'enterBothDates';

  if (endMs < startMs) return 'endAfterStart';

  // Both bounds are whole days, so the inclusive span of `from`…`to` is one day
  // longer than the difference between their midnights. Without the extra day
  // this accepts a 184-day period that the server then rejects.
  if (endMs - startMs + DAY_MS > MAX_PERIOD_DAYS * DAY_MS) return 'maxRange';

  return null;
}

export function reportUrl(apiBaseUrl, format, start, end) {
  const spec = REPORT_FORMATS[format];
  if (!spec) throw new Error(`Unknown report format: ${format}`);
  return `${apiBaseUrl}/api/${spec.path}` +
    `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
}

/**
 * The `filename` out of a `Content-Disposition` header, or `fallback`.
 *
 * The header is unreadable when the dashboard and the API are not same-origin —
 * `npm start` on :8080 against https://samst.live — because Content-Disposition
 * is not a CORS-safelisted response header and nginx exposes nothing extra.
 * Deployed they are same-origin behind the same server and the server's own name
 * comes through, which is the case that matters. The fallback is what keeps a
 * developer's two downloads from being given the same name.
 */
export function filenameFromDisposition(header, fallback) {
  return sanitiseFilename(header ? parseDisposition(header) : null) ?? fallback;
}

function parseDisposition(header) {
  // RFC 5987 `filename*` first, so it wins where a server sends both forms.
  // `filename\s*=` below cannot match `filename*=` — the `*` is neither
  // whitespace nor `=` — so the ordering is a preference, not a correctness
  // requirement.
  const extended = /filename\*\s*=\s*([^;]+)/i.exec(header);
  if (extended) {
    const value = extended[1].trim();
    const afterCharset = /^[^']*'[^']*'(.*)$/.exec(value);
    try {
      return decodeURIComponent(afterCharset ? afterCharset[1] : value);
    } catch {
      // A malformed percent-escape is not a reason to fail a download that has
      // already succeeded — fall through to the plain form, then the fallback.
    }
  }

  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(header);
  if (quoted) return quoted[1];

  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  return bare ? bare[1].trim() : null;
}

// Path separators, the characters Windows refuses in a name, and the control
// range. Built from a string so the source file carries no literal control
// characters; an ASCII allowlist would have been shorter but would also mangle
// the diacritics of a Montenegrin filename, and this application is bilingual.
//
// eslint-disable-next-line no-control-regex
const UNSAFE_NAME_CHARS = new RegExp('[\\u0000-\\u001f\\u007f\\\\/:*?"<>|]', 'g');

// A server-set name still reaches an `<a download>`. `document_id` is a date
// pair today and cannot produce any of the above; the point is that this holds
// if that id ever changes shape. A leading run of dots is stripped too, so
// neither a dotfile nor the remains of a `..` segment survives as a name.
function sanitiseFilename(name) {
  if (!name) return null;
  const cleaned = name.trim().replace(UNSAFE_NAME_CHARS, '_').replace(/^[._-]+/, '');
  return cleaned.length ? cleaned : null;
}

/**
 * Fetches one format of the corridor report and hands it to the browser.
 * Resolves with the filename used; throws on anything that is not a report.
 *
 * `onStage` receives the `{pct, label}` shape TowerDriftChart's `exportProgress`
 * carries, with `label` a translation key. The two stages are real rather than a
 * timer: the fetch resolves when the *headers* arrive, which is the moment the
 * server finished assembling and rendering, and `.blob()` after it is the
 * transfer. A month-long report spends nearly all of its time in the first
 * stage, which is why that is the one whose label says "assembling".
 */
export async function downloadReport({
  apiBaseUrl,
  format,
  start,
  end,
  onStage = () => {},
  fetchImpl = apiFetch,
  deliver = downloadBlob,
}) {
  const spec = REPORT_FORMATS[format];
  if (!spec) throw new Error(`Unknown report format: ${format}`);

  onStage({ pct: 10, label: 'reportAssembling' });
  const res = await fetchImpl(reportUrl(apiBaseUrl, format, start, end));

  // Every non-2xx is a failure to *produce* the report, including the 400 that
  // periodError() above should already have prevented. None of them is an empty
  // report: a period with no readings is answered with a document that lists the
  // devices which reported nothing rather than with an error, so a caller must
  // never render one of these as "no data" (#143).
  if (!res.ok) throw new Error(`Report request failed: HTTP ${res.status}`);

  onStage({ pct: 70, label: 'reportDownloading' });
  const blob = await res.blob();

  // The routes render to a Buffer before touching the response precisely so a
  // failed render is a 500 rather than a truncated file. A zero-byte 200
  // therefore should not happen — and if it ever does, saving it is worse than
  // reporting the failure, because a corrupt PDF is discovered by the reader
  // after it has been forwarded.
  if (blob.size === 0) throw new Error('Report response was empty');

  const filename = filenameFromDisposition(
    res.headers?.get?.('Content-Disposition'),
    `samst-corridor-${start}_${end}.${spec.extension}`
  );
  deliver(blob, filename);

  onStage({ pct: 100, label: 'reportDone' });
  return filename;
}
